import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pickBy } from 'lodash';
import StaffModal from './StaffModal';
import FbCircularProgress from '../../components/FbCircularProgress';
import NoListItems from '../../components/NoListItems';
import ActionStatusModal from '../../components/ActionStatusModal';
import TextButton from '../../components/TextButton';
import Button from '../../components/Button';
import { colors } from '../../constants/styleConstants';
import { deleteEntity, getPrivacyOptionsAction, getWholeList, updateOne } from '../App/actions';
import {
  COMPANY_CERTAIN_EMPLOYEE,
  COMPANY_EMPLOYEES,
  PRIVACY_OPTIONS,
  FESTIVAL_EMPLOYEES,
  FESTIVAL_CERTAIN_EMPLOYEE,
} from '../../constants/endpointConstants';
import { deleteStaffAction, getStaffAction, updateStaffAction } from './actions';
import { TP } from '../../constants/consts';
import { selectStaff, selectStaffUpdating } from './selectors';
import { selectPrivacyOptions } from '../App/selectors';
import StaffCard from './StaffCard';
import { EditLayoutHeader } from '../../components/EditComponents/EditLayout/EditLayoutHeader';
import { EDITING_ENTITY_TYPES } from '../../utils/editCommon';

const useStyles = makeStyles(() => ({
  error: {
    color: colors.red,
  },
}));

const endpoints = {
  company: {
    getStaffList: COMPANY_EMPLOYEES,
    update: COMPANY_CERTAIN_EMPLOYEE,
  },
  festival: {
    getStaffList: FESTIVAL_EMPLOYEES,
    update: FESTIVAL_CERTAIN_EMPLOYEE,
  },
};

const EditStaff = ({ entity, staff, privacyOptions, type, staffUpdating, title, subtitle }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editableStaff, setEditableStaff] = React.useState(null);
  const [removedStaff, setRemovedStaff] = React.useState(null);
  const [confirmRemoveModalOpened, setConfirmRemoveModalOpened] = React.useState(false);

  React.useEffect(() => {
    const getStaffEndpoint = endpoints[type]?.getStaffList;
    dispatch(
      getWholeList({
        endpoint: getStaffEndpoint(entity?.id),
        sagaRoutine: getStaffAction,
        params: { queryParams: { validity: 0 } },
      }),
    );
    dispatch(
      getWholeList({
        endpoint: PRIVACY_OPTIONS,
        sagaRoutine: getPrivacyOptionsAction,
        params: {},
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStaffPrivacy = (privacyOptionId, updatedStaff) => {
    const updateStaffEndpoint = endpoints[type]?.update;
    dispatch(
      updateOne({
        endpoint: updateStaffEndpoint(updatedStaff?.id),
        sagaRoutine: updateStaffAction,
        params: {
          ...pickBy(updatedStaff, (val, key) => key === 'company' || key === 'profile'),
          privacyAttributes: [{ id: privacyOptionId }],
        },
      }),
    );
  };

  const removeStaff = () => {
    const updateStaffEndpoint = endpoints[type]?.update;
    setConfirmRemoveModalOpened(false);
    dispatch(
      deleteEntity({
        endpoint: updateStaffEndpoint(),
        sagaRoutine: deleteStaffAction,
        callback: () => setRemovedStaff(null),
        id: removedStaff?.id,
      }),
    );
  };

  return (
    <>
      <EditLayoutHeader
        title={title}
        subtitle={subtitle}
        actions={
          <Button variant="outlined" color="secondary" onClick={() => setEditableStaff({})}>
            {t(`${TP}.FN_ADD_CONTACT`)}
          </Button>
        }
      />
      {staff?.loading ? (
        <FbCircularProgress />
      ) : (
        !!staff?.data?.length &&
        staff.data.map(s => (
          <StaffCard
            key={s?.id}
            staffMember={s}
            privacyOptions={privacyOptions}
            onDelete={() => {
              setRemovedStaff(s);
              setConfirmRemoveModalOpened(true);
            }}
            onEdit={() => setEditableStaff(s)}
            onPrivacyChange={v => updateStaffPrivacy(v?.value, s)}
            isLoading={staffUpdating && removedStaff?.id === s?.id}
            type={type}
          />
        ))
      )}
      {!staff.loading && !staff?.data?.length && !staff.error && staff?.loadedOnce && (
        <NoListItems text={t(`${TP}.FN_NO_CONTACTS_ADDED`)} />
      )}
      {!staff.loading && staff?.loadedOnce && staff.error && (
        <NoListItems className={classes.error} text={staff.error} />
      )}
      {Boolean(editableStaff) && (
        <StaffModal
          staff={editableStaff}
          onClose={() => setEditableStaff(null)}
          companyId={entity.id}
          staffEntityEndpoint={endpoints[type]?.update}
          type={type}
        />
      )}
      {confirmRemoveModalOpened && (
        <ActionStatusModal
          isOpened
          onClose={() => {
            setRemovedStaff(null);
            setConfirmRemoveModalOpened(false);
          }}
          type="warning"
          title={t(`${TP}.FN_REMOVE_STAFF_CONFIRM`)}
          buttons={[
            <TextButton
              onClick={() => {
                setRemovedStaff(null);
                setConfirmRemoveModalOpened(false);
              }}
              key="cancel-button"
            >
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button onClick={removeStaff} key="approve-button">
              {t(`${TP}.FN_REMOVE`)}
            </Button>,
          ]}
        />
      )}
    </>
  );
};

EditStaff.propTypes = {
  entity: PropTypes.object.isRequired,
  title: PropTypes.string,
  type: PropTypes.oneOf([EDITING_ENTITY_TYPES.FESTIVAL, EDITING_ENTITY_TYPES.COMPANY]),
};

EditStaff.defaultProps = {
  title: `${TP}.FN_STAFF_TITLE`,
};

const mapStateToProps = createStructuredSelector({
  staff: selectStaff(),
  privacyOptions: selectPrivacyOptions(),
  staffUpdating: selectStaffUpdating(),
});

const withConnect = connect(mapStateToProps, null);
export default withConnect(EditStaff);
